import { groupDB, groupsApis } from '@/configs/apis/groups';
import { useSessionContext } from '@/contexts/session/context';
import { safeJson } from '@/scripts/utils';
import { GroupEncryptionStatus } from '@/configs/apis/groups/interface';
import type { UseGroupEncryptionResult } from '../useGroupEncryption/types';
import type { ChatMessageMessageId } from '../useChatMessages/types';
import type { ChatChatId } from '../useChats/types';
import {
  type ChatGroupParams,
  type ChatGroupGroupId,
  type ChatGroupMap,
  type ChatGroupSettingsParams,
  type ChatGroupSettingsMap,
  type ChatGroupInfo,
  type ChatGroupMembersMap,
  type ChatGroupMemberParamsArgs,
  type ChatGroupMemberIdArgs,
  type ChatGroupMember,
  type ChatGroupMemberInfoMap,
  type ChatGroupSettingsPropArgs,
  type ChatGroupHalfMemberArgs,
  type ChatGroupSettings,
  type ChatGroupReadStateMap,
  type ChatGroupReadStateParams,
  type ChatGroupReadStateId,
  type ChatGroupReadStateReaderArgParams,
  type DBChatGroupReadState,
  ChatGroupMemberRemoved,
} from './types';
import { genChatMember } from './utils';

interface ChatGroupState {
  groupMap: ChatGroupMap;
  membersMap: ChatGroupMembersMap;
  settingsMap: ChatGroupSettingsMap;
  readStateMap: ChatGroupReadStateMap;
}

type ChatGroupStateActions =
  | {
      type: 'init';
      payload: {
        groups: ChatGroupParams[];
        members: { group_id: string; members: ChatGroupMember[] }[];
        settings: { group_id: string; settings: ChatGroupSettings }[];
      };
    }
  | { type: 'insertGroups'; payload: ChatGroupParams[] }
  | { type: 'removeGroups'; payload: ChatGroupGroupId[] }
  | { type: 'insertSettings'; payload: ChatGroupSettingsParams[] }
  | { type: 'removeSettings'; payload: ChatGroupSettingsPropArgs[] }
  | {
      type: 'getMembersByChatId';
      payload: ChatChatId;
      cb: (members: ChatGroupMember[]) => void;
    }
  | { type: 'insertMembers'; payload: ChatGroupMemberParamsArgs[] }
  | { type: 'removeMembers'; payload: ChatGroupMemberIdArgs[] }
  | { type: 'insertReadState'; payload: ChatGroupReadStateParams[] }
  | { type: 'removeReadState'; payload: ChatGroupReadStateId[] }
  | { type: 'insertReadStateReader'; payload: ChatGroupReadStateReaderArgParams[] }
  | {
      type: 'getReadState';
      payload: ChatMessageMessageId;
      cb: (readState: DBChatGroupReadState) => void;
    };

function genChatGroupState(): ChatGroupState {
  return {
    groupMap: {},
    membersMap: {},
    settingsMap: {},
    readStateMap: {},
  };
}

export function useChatGroupState() {
  const [state, dispatch] = useReducer(
    (state: ChatGroupState, action: ChatGroupStateActions) => {
      let state_ = { ...state };
      switch (action.type) {
        case 'init': {
          const { groups, members, settings } = action.payload;
          state_ = genChatGroupState();
          state_.groupMap = groups.reduce((m, g) => {
            m[g.group_id] = {
              ...m[g.group_id],
              ...g,
            };
            return m;
          }, {} as ChatGroupMap);
          state_.membersMap = members.reduce((m, g) => {
            m[g.group_id] = {
              members: g.members,
              normalMembers: g.members.filter(
                (member) => member.removed === ChatGroupMemberRemoved.Normal
              ),
              membersInfoMap: g.members.reduce((m, g, index) => {
                m[g.user_id] = {
                  index,
                  member: g,
                };
                return m;
              }, {} as ChatGroupMemberInfoMap),
            };
            return m;
          }, {} as ChatGroupMembersMap);
          state_.settingsMap = settings.reduce((m, s) => {
            m[s.group_id] = {
              ...s.settings,
            };
            return m;
          }, {} as ChatGroupSettingsMap);
          break;
        }
        case 'insertGroups': {
          state_.groupMap = action.payload.reduce(
            (m, g) => {
              m[g.group_id] = {
                ...m[g.group_id],
                ...g,
              };
              return m;
            },
            {
              ...state_.groupMap,
            }
          );
          break;
        }
        case 'removeGroups': {
          const ids = action.payload;
          state_.groupMap = { ...state_.groupMap };
          for (const id of ids) {
            delete state_.groupMap[id];
          }
          break;
        }
        case 'insertSettings': {
          state_.settingsMap = action.payload.reduce(
            (m, s) => {
              m[s.group_id] = {
                ...m[s.group_id],
                ...s,
              };
              return m;
            },
            {
              ...state_.settingsMap,
            }
          );
          break;
        }
        case 'removeSettings': {
          const ids = action.payload;
          state_.settingsMap = { ...state_.settingsMap };
          for (const { id, args } of ids) {
            let settings = state_.settingsMap[id];
            if (settings) {
              settings = { ...settings };
              for (const props of args) {
                delete settings[props as keyof ChatGroupSettings];
              }
              state_.settingsMap[id] = settings;
            }
          }
          break;
        }
        case 'insertMembers': {
          for (const { id, args } of action.payload) {
            if (args.length) {
              const oldItem = state_.membersMap[id];
              const members = oldItem ? [...oldItem.members] : [];
              const membersInfoMap = oldItem?.membersInfoMap || {};
              const addMembers: ChatGroupMember[] = [];

              for (const memberParams of args) {
                const { user_id } = memberParams;
                const memberInfo = membersInfoMap[user_id];
                if (memberInfo) {
                  const { index, member: oldMember } = memberInfo;
                  members.splice(index, 1, { ...oldMember, ...memberParams });
                } else {
                  addMembers.push(genChatMember(memberParams));
                }
              }

              setMembers(id, members.concat(addMembers));
            }
          }
          break;
        }
        case 'removeMembers': {
          for (const { id, args } of action.payload) {
            if (args.length) {
              const membersItem = state_.membersMap[id];
              if (membersItem) {
                const members = [...membersItem.members];
                const membersInfoMap = membersItem.membersInfoMap;

                const indexes: number[] = [];

                for (const id of args) {
                  const memberInfo = membersInfoMap[id];
                  if (memberInfo) {
                    const { index } = memberInfo;
                    indexes.push(index);
                  }
                }

                indexes
                  .sort((a, b) => b - a)
                  .forEach((index) => {
                    // members.splice(index, 1);
                    members[index].removed = ChatGroupMemberRemoved.Removed;
                  });

                setMembers(id, members);
              }
            }
          }
          break;
        }
        case 'getMembersByChatId':
          action.cb(state.membersMap[action.payload]?.normalMembers || []);
          break;
        case 'insertReadState': {
          state_.readStateMap = action.payload.reduce(
            (m, g) => {
              m[g.message_id] = {
                ...m[g.message_id],
                ...g,
              };
              return m;
            },
            {
              ...state_.readStateMap,
            }
          );
          break;
        }
        case 'removeReadState': {
          const ids = action.payload;
          state_.readStateMap = { ...state_.readStateMap };
          for (const id of ids) {
            delete state_.readStateMap[id];
          }
          break;
        }
        case 'insertReadStateReader': {
          state_.readStateMap = action.payload.reduce(
            (m, g) => {
              const prev = m[g.message_id];

              const o = Object.assign(
                safeJson(prev?.reader_ids, {}),
                g.args.reduce(
                  (acc, userId) => {
                    acc[userId] = 1;
                    return acc;
                  },
                  {} as Record<string, 1>
                )
              );

              m[g.message_id] = {
                ...prev,
                reader_ids: JSON.stringify(o),
              };
              return m;
            },
            {
              ...state_.readStateMap,
            }
          );
          break;
        }
        case 'getReadState': {
          action.cb(state.readStateMap[action.payload]);
          return state;
        }
      }

      function setMembers(id: ChatGroupGroupId, members: ChatGroupMember[]) {
        state_.membersMap = { ...state_.membersMap };

        const membersInfoMap: ChatGroupMemberInfoMap = {};

        members.forEach((member, index) => {
          const info = {
            index,
            member,
          };
          membersInfoMap[member.user_id] = info;
        });

        const normalMembers = members.filter(
          (member) => member.removed === ChatGroupMemberRemoved.Normal
        );

        state_.membersMap[id] = {
          members,
          normalMembers,
          membersInfoMap,
        };
      }

      return state_;
    },
    genChatGroupState()
  );

  const initGroups = useCallback(
    ({
      groups,
      members,
      settings,
    }: {
      groups: ChatGroupParams[];
      members: { group_id: string; members: ChatGroupMember[] }[];
      settings: { group_id: string; settings: ChatGroupSettings }[];
    }) => {
      dispatch({ type: 'init', payload: { groups, members, settings } });
    },
    []
  );

  const insertGroups = useCallback((args: ChatGroupParams[]) => {
    dispatch({ type: 'insertGroups', payload: args });
  }, []);

  const removeGroups = useCallback((ids: ChatGroupGroupId[]) => {
    dispatch({ type: 'removeGroups', payload: ids });
  }, []);

  const insertSettings = useCallback((args: ChatGroupSettingsParams[]) => {
    dispatch({ type: 'insertSettings', payload: args });
  }, []);

  const removeSettings = useCallback((ids: ChatGroupSettingsPropArgs[]) => {
    dispatch({ type: 'removeSettings', payload: ids });
  }, []);

  const getMembersByUserId = useCallback(
    (args: ChatGroupMemberIdArgs[]) => {
      return args.map(({ id, args }) => {
        return {
          id,
          args: args.map(
            (user_id) =>
              state.membersMap[id]?.membersInfoMap[user_id]?.member || user_id
          ),
        };
      }) as ChatGroupHalfMemberArgs[];
    },
    [state.membersMap]
  );

  const getMembersByChatId = useCallback((chat_id: ChatChatId) => {
    return new Promise<ChatGroupMember[]>((resolve) => {
      dispatch({ type: 'getMembersByChatId', payload: chat_id, cb: resolve });
    });
  }, []);

  const insertMembers = useCallback((args: ChatGroupMemberParamsArgs[]) => {
    dispatch({ type: 'insertMembers', payload: args });
  }, []);

  const removeMembers = useCallback((args: ChatGroupMemberIdArgs[]) => {
    dispatch({ type: 'removeMembers', payload: args });
  }, []);

  const getReadState = useCallback((message_id: ChatMessageMessageId) => {
    return new Promise<DBChatGroupReadState | undefined>((resolve) => {
      dispatch({ type: 'getReadState', payload: message_id, cb: resolve });
    });
  }, []);

  const insertReadState = useCallback((args: ChatGroupReadStateParams[]) => {
    dispatch({ type: 'insertReadState', payload: args });
  }, []);

  const removeReadState = useCallback((args: ChatGroupReadStateId[]) => {
    dispatch({ type: 'removeReadState', payload: args });
  }, []);

  const insertReadStateReader = useCallback(
    (args: ChatGroupReadStateReaderArgParams[]) => {
      dispatch({ type: 'insertReadStateReader', payload: args });
    },
    []
  );

  const getGroup = useCallback<(id: ChatGroupGroupId) => ChatGroupInfo | undefined>(
    (id) => {
      const group = state.groupMap[id];
      if (group) {
        const settings = state.settingsMap[id] || {};
        const member = state.membersMap[id] || {};
        return {
          ...group,
          settings,
          members: member.members,
        };
      }
    },
    [state]
  );

  const value = useMemo(
    () => ({
      state,
      dispatch,
      initGroups,
      insertGroups,
      removeGroups,
      insertSettings,
      removeSettings,
      getMembersByUserId,
      insertMembers,
      removeMembers,
      getReadState,
      insertReadState,
      removeReadState,
      insertReadStateReader,
      getGroup,
      getMembersByChatId,
    }),
    [
      getGroup,
      initGroups,
      insertGroups,
      getMembersByUserId,
      insertMembers,
      insertSettings,
      removeGroups,
      removeMembers,
      removeSettings,
      getReadState,
      insertReadState,
      removeReadState,
      insertReadStateReader,
      state,
      getMembersByChatId,
    ]
  );

  return value;
}

export function useChatGroupDB() {
  const initGroups = useCallback(() => {
    return groupDB.getAllGroups();
  }, []);

  const getGroups = useCallback((ids: ChatGroupGroupId[]) => {
    return groupDB.getGroups(ids);
  }, []);

  const insertGroups = useCallback((args: ChatGroupParams[]) => {
    return groupDB.insertGroups(args);
  }, []);

  const updateGroups = useCallback((args: ChatGroupParams[]) => {
    return groupDB.updateGroups(args);
  }, []);

  const removeGroups = useCallback((ids: ChatGroupGroupId[]) => {
    return groupDB.removeGroups(ids);
  }, []);

  const getSettings = useCallback(
    (id: ChatGroupGroupId): Promise<ChatGroupSettings> => {
      return groupDB.getSettings(id);
    },
    []
  );

  const insertSettings = useCallback((args: ChatGroupSettingsParams[]) => {
    return groupDB.insertSettings(args);
  }, []);

  const removeSettings = useCallback((ids: ChatGroupSettingsPropArgs[]) => {
    return groupDB.removeSettings(ids);
  }, []);

  const getMembers = useCallback(
    (params: {
      group_id: string;
      limit: number;
      offset: number;
      include_removed?: boolean;
    }) => {
      return groupDB.getMembers(params);
    },
    []
  );

  const getMembersByUserId = useCallback((list: ChatGroupHalfMemberArgs[]) => {
    return groupDB.getMembersByUserId(list);
  }, []);

  const insertMembers = useCallback((args: ChatGroupMemberParamsArgs[]) => {
    return groupDB.insertMembers(args);
  }, []);

  const removeMembers = useCallback((args: ChatGroupMemberIdArgs[]) => {
    return groupDB.removeMembers(args);
  }, []);

  const getReadState = useCallback((message_id: ChatMessageMessageId) => {
    return groupDB.getReadStateByMessageId(message_id);
  }, []);

  const insertReadState = useCallback((args: ChatGroupReadStateParams[]) => {
    return groupDB.insertReadState(args);
  }, []);

  const removeReadState = useCallback((args: ChatGroupReadStateId[]) => {
    return groupDB.removeReadState(args);
  }, []);

  const insertReadStateReader = useCallback(
    (args: ChatGroupReadStateReaderArgParams[]) => {
      return groupDB.insertReadStateReader(args);
    },
    []
  );

  const value = useMemo(
    () => ({
      initGroups,
      getGroups,
      insertGroups,
      updateGroups,
      removeGroups,
      getSettings,
      insertSettings,
      removeSettings,
      getMembers,
      getMembersByUserId,
      insertMembers,
      removeMembers,
      getReadState,
      insertReadState,
      removeReadState,
      insertReadStateReader,
    }),
    [
      initGroups,
      getGroups,
      insertGroups,
      updateGroups,
      removeGroups,
      getSettings,
      insertSettings,
      removeSettings,
      getMembers,
      getMembersByUserId,
      insertMembers,
      removeMembers,
      getReadState,
      insertReadState,
      removeReadState,
      insertReadStateReader,
    ]
  );

  return value;
}

export function useChatGroup({
  groupEncryption,
}: {
  groupEncryption: UseGroupEncryptionResult;
}) {
  const chatGroupState = useChatGroupState();
  const chatGroupDB = useChatGroupDB();
  const { session } = useSessionContext();

  const initGroups = useCallback(async () => {
    const groups: ChatGroupParams[] = await chatGroupDB.initGroups();
    const groudIds = groups
      .filter((chat) => chat.is_encryption === GroupEncryptionStatus.IS)
      .map((group) => group.group_id);
    if (groudIds.length) {
      await groupEncryption.initLoadGroups(groudIds);
    }
    const members = await Promise.all(
      groups.map(async (group) => {
        const members = await chatGroupDB.getMembers({
          group_id: group.group_id,
          limit: -1,
          offset: 0,
          include_removed: true,
        });
        return {
          group_id: group.group_id,
          members,
        };
      })
    );
    const settings = await Promise.all(
      groups.map(async (group) => {
        const settings = await chatGroupDB.getSettings(group.group_id);
        return { group_id: group.group_id, settings };
      })
    );
    chatGroupState.initGroups({
      groups,
      members,
      settings,
    });
  }, [chatGroupDB, chatGroupState, groupEncryption]);

  const getMembers = useCallback(
    async (params: {
      group_id: string;
      limit: number;
      offset: number;
      include_removed?: boolean;
    }) => {
      const members = await chatGroupDB.getMembers(params);
      return members;
    },
    [chatGroupDB]
  );

  const insertGroups = useCallback(
    async (args: ChatGroupParams[]) => {
      await chatGroupDB.insertGroups(args);
      chatGroupState.insertGroups(args);
    },
    [chatGroupDB, chatGroupState]
  );

  const updateGroups = useCallback(
    async (args: ChatGroupParams[]) => {
      await chatGroupDB.updateGroups(args);
      chatGroupState.insertGroups(args);
    },
    [chatGroupDB, chatGroupState]
  );

  const removeGroups = useCallback(
    async (ids: ChatGroupGroupId[]) => {
      await chatGroupDB.removeGroups(ids);
      chatGroupState.removeGroups(ids);
    },
    [chatGroupDB, chatGroupState]
  );

  const insertSettings = useCallback(
    async (args: ChatGroupSettingsParams[]) => {
      await chatGroupDB.insertSettings(args);
      chatGroupState.insertSettings(args);
    },
    [chatGroupDB, chatGroupState]
  );

  const removeSettings = useCallback(
    async (ids: ChatGroupSettingsPropArgs[]) => {
      await chatGroupDB.removeSettings(ids);
      chatGroupState.removeSettings(ids);
    },
    [chatGroupDB, chatGroupState]
  );

  const getMembersByUserId = useCallback(
    async (args: ChatGroupMemberIdArgs[]) => {
      const stateMembers = chatGroupState.getMembersByUserId(args);
      const res = await chatGroupDB.getMembersByUserId(stateMembers);
      chatGroupState.insertMembers(
        res.map(({ id, DBArgs }) => ({ id, args: DBArgs.filter(Boolean) }))
      );
      return res;
    },
    [chatGroupDB, chatGroupState]
  );

  const insertMembers = useCallback(
    async (args: ChatGroupMemberParamsArgs[], triggerApi: boolean = true) => {
      if (triggerApi) {
        await Promise.all(
          args.map((arg) =>
            arg.is_encryption === GroupEncryptionStatus.IS
              ? groupEncryption.addMembersWithUpdateInfo({
                  groupId: arg.id,
                  uids: arg.args.map((member) => member.user_id),
                })
              : groupsApis.inviteMembers(
                  arg.id,
                  arg.args.map((member) => member.user_id)
                )
          )
        );
      }

      await chatGroupDB.insertMembers(args);
      chatGroupState.insertMembers(args);
    },
    [chatGroupDB, chatGroupState, groupEncryption]
  );

  const removeMembers = useCallback(
    async (args: ChatGroupMemberIdArgs[], triggerApi: boolean = true) => {
      let list: ChatGroupMemberIdArgs[] = args;
      if (triggerApi) {
        const res = await Promise.all(
          list.map((arg) =>
            arg.is_encryption === GroupEncryptionStatus.IS
              ? groupEncryption.removeMembersWithUpdateInfo({
                  groupId: arg.id,
                  uids: arg.args,
                })
              : groupsApis.removeMembers(arg.id, arg.args)
          )
        );
        const removeFailList = res?.flatMap((item) => item.remove_fail_list);
        if (removeFailList?.length > 0) {
          const removeSuccessList = args
            .map((arg) => ({
              ...arg,
              args: arg.args.filter((uid) => !removeFailList.includes(uid)),
            }))
            .filter((arg) => arg.args.length > 0);
          list = removeSuccessList;
        }
      }
      await chatGroupDB.removeMembers(list);
      chatGroupState.removeMembers(list);
    },
    [chatGroupDB, chatGroupState, groupEncryption]
  );

  const getReadState = useCallback(
    async (message_id: ChatMessageMessageId) => {
      let state = await chatGroupState.getReadState(message_id);
      if (!state) {
        state = await groupDB.getReadStateByMessageId(message_id);
        if (state) {
          chatGroupState.insertReadState([state]);
        }
      }
      if (state) {
        const reader_ids_value = Object.keys(safeJson(state.reader_ids, {}));
        const expect_read_list_value = state?.expect_read_list?.split(',') || [];
        return {
          ...state,
          reader_ids_value,
          expect_read_list_value,
        };
      }
    },
    [chatGroupState]
  );

  const insertReadState = useCallback(
    async (args: ChatGroupReadStateParams[]) => {
      await chatGroupDB.insertReadState(args);
      chatGroupState.insertReadState(args);
    },
    [chatGroupDB, chatGroupState]
  );

  const removeReadState = useCallback(
    async (args: ChatGroupReadStateId[]) => {
      await chatGroupDB.removeReadState(args);
      chatGroupState.removeReadState(args);
    },
    [chatGroupDB, chatGroupState]
  );

  const insertReadStateReader = useCallback(
    async (args: ChatGroupReadStateReaderArgParams[]) => {
      await chatGroupDB.insertReadStateReader(args);
      chatGroupState.insertReadStateReader(args);
    },
    [chatGroupDB, chatGroupState]
  );

  const initReadStateReceiver = useCallback(
    async (params: ChatGroupReadStateParams) => {
      const oldIds = (await getReadState(params.message_id))?.expect_read_list;
      if (!oldIds && params.expect_read_list) {
        await insertReadState([
          {
            message_id: params.message_id,
            expect_read_list: params.expect_read_list,
          },
        ]);
      }
    },
    [getReadState, insertReadState]
  );

  /**
   * @deprecated replace by chatGroupState.state.settingsMap
   */
  const getSettings = useCallback(
    async (chat_id: ChatGroupGroupId): Promise<ChatGroupSettings> => {
      return chatGroupState.state.settingsMap[chat_id] ?? {};
    },
    [chatGroupState.state.settingsMap]
  );

  const refs = useRef({
    initGroups,
  });

  const value = useMemo(
    () => ({
      chatGroupState,
      chatGroupDB,
      getMembersByChatId: chatGroupState.getMembersByChatId,
      getMembers,
      getSettings,
      insertGroups,
      updateGroups,
      removeGroups,
      insertSettings,
      removeSettings,
      getMembersByUserId,
      insertMembers,
      removeMembers,
      getReadState,
      insertReadState,
      removeReadState,
      insertReadStateReader,
      initReadStateReceiver,
    }),
    [
      chatGroupState,
      chatGroupDB,
      getMembers,
      getSettings,
      insertGroups,
      updateGroups,
      removeGroups,
      insertSettings,
      removeSettings,
      getMembersByUserId,
      insertMembers,
      removeMembers,
      getReadState,
      insertReadState,
      removeReadState,
      insertReadStateReader,
      initReadStateReceiver,
    ]
  );

  useEffect(() => {
    if (session.user?.userId) {
      refs.current.initGroups();
    }
  }, [refs, session]);

  return value;
}
